import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import {
    Avatar,
    ButtonBase,
    CircularProgress,
    makeStyles,
} from '@material-ui/core';

import { activityLabels, patientProgramElementGroups } from '../constants';
import { Image } from '../../youwell-common/components';
import urls from '../../app/urls';
import TrackedLink from './TrackedLink';
import apiUrls from '../../youwell-common/apiUrls';

const elementSize = 50;

const useStyles = makeStyles(theme => ({
    root: {
        height: 2 * elementSize + 16 + 16, // iconHeight + margin*4 + padding * 2
        background: `linear-gradient(180deg, ${theme.youwell.colors.secondaryBackground} 50%, transparent 50%)`,
        padding: theme.spacing(1, 4),
        display: 'flex',
        justifyContent: 'space-between',
        overflowY: 'hidden',
        overflowX: 'auto',

        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 1),
        },
    },
    module: {
        position: 'relative',
        margin: theme.spacing(0.5, 1),

        '&:first-child': {
            marginLeft: 0,
        },
        '&:last-child': {
            marginRight: 0,
        },
        '&$disabled': {
            opacity: 0.7,
        },
    },
    progress: {
        position: 'absolute',
        zIndex: 1,
        height: elementSize,
        width: elementSize,
        pointerEvents: 'none',
    },
    button: {
        margin: 1,
        borderRadius: '100%',
        border: `2px dashed ${theme.palette.primary.main}`,
        overflow: 'hidden',
        height: elementSize - 2,
        width: elementSize - 2,

        '&$current': {
            margin: 4,
            height: 2 * elementSize - 8,
            width: 2 * elementSize - 8,
        },
    },
    image: {
        height: '100%',
        width: '100%',
        objectFit: 'cover',
    },
    avatar: {
        background: theme.palette.common.white,
        color: theme.palette.text.primary,
    },
    completed: {},
    current: {},
    disabled: {},
}), { name: 'ModuleProgress' });

const ModuleProgress = ({ className }) => {
    const classes = useStyles();

    // const translate = useSelector(state => getTranslate(state.localize));
    const currentProgram = useSelector(state => state.programStore.currentProgram);

    if (!currentProgram?.groupedElements) {
        return null;
    }

    const nextElementIndex = currentProgram.groupedElements[patientProgramElementGroups.main]?.findIndex(d => !d.completed && !d.disabled);
    const mainElements = currentProgram.groupedElements[patientProgramElementGroups.main] || [];

    return (
        <div className={classnames(classes.root, className)}>
            {mainElements.map((element, i) => (
                <div className={classnames(classes.module, { [classes.disabled]: element.disabled })} key={element.id} title={element.name}>
                    <CircularProgress thickness={3} size={i === nextElementIndex ? elementSize * 2 : elementSize} className={classes.progress} variant="determinate" value={element.progress * 100} color="secondary" />
                    <ButtonBase
                        className={classnames(classes.button, { [classes.completed]: element.completed, [classes.current]: i === nextElementIndex })}
                        to={`${urls.inside.program}/${element.elementGroup}/${element.indexInGroup}/${urls.inside.parts.resumeLatest}`}
                        component={TrackedLink}
                        label={`${activityLabels.moduleProgress.link}/${element.indexInGroup}`}
                        disabled={element.disabled}
                    >
                        {element.module?.moduleImageId ? <Image className={classes.image} src={`${apiUrls.baseUrl}/${apiUrls.media.file}/${element.module.moduleImageId}`} />
                            : (
                                <Avatar className={classnames(classes.image, classes.avatar)}>{element.name?.charAt(0).toUpperCase()}</Avatar>
                            )}
                    </ButtonBase>
                </div>
            ))}
        </div>
    );
};

ModuleProgress.propTypes = {
    className: PropTypes.string,
};

ModuleProgress.defaultProps = {
    className: null,
};


export default ModuleProgress;
